import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useWorkspace } from '../contexts/WorkspaceContext'
import { ApiError, apiJson, type BoundSource } from '../lib/api'
import { Failed, PendingButton } from '../components/pending'
import { SignInPage } from './SignInPage'
import { deviceAppLabel, when, type FieldDevice } from './DevicesPage'
import signIn from './SignInPage.module.css'
import styles from './NoAccessPage.module.css'
import own from './DevicePairPage.module.css'

// A Dataphyll Field tablet's QR (routes/field-devices.js, migrations 181 and 182): /devices/pair/<code>. Signed out, it
// is the sign-in screen saying what it is for. Signed in, it lists the workspaces where the caller may register a
// device: those they are a system user of, and those where their Crew sign-in is a Receipter or Supervisor. They choose
// what the device is (only what that workspace's Client Setup ticks), name it and register it. The tablet is polling,
// and carries on by itself.

export const isDevicePairPath = (pathname: string) => pathname.startsWith('/devices/pair/')

interface Pairing {
  pair_code: string
  device_id: string
  device_type: string | null
  form_factor: 'tablet' | 'phone' | null
  model: string | null
  platform: string | null
  app_version: string | null
  status: 'pending' | 'approved' | 'denied' | 'expired'
  client_id: string | null
  client_name: string | null
  device_name: string | null
  decided_by_email: string | null
  decided_at: string | null
  expires_at: string
}

interface DeviceType {
  id: 'field_station' | 'preassign' | 'supervisor'
  label: string
  enabled: boolean
}

interface Place {
  id: string
  name: string
  role: string
  as: 'system_user' | 'crew'
  can_register: boolean
  existing: FieldDevice | null
  device_types: DeviceType[]
}

const TYPE_BLURB: Record<DeviceType['id'], string> = {
  field_station: 'A scale that weighs each bin.',
  preassign: 'Puts a worker’s name on each item before it is weighed.',
  supervisor: 'A supervisor’s device in the field.',
}

// What a device is registered as in a place: what it asked to be if that is set up there, else the first that is.
const typeFor = (place: Place | null | undefined, asked: string | null | undefined) => {
  const open = (place?.device_types || []).filter((t) => t.enabled)
  return (open.find((t) => t.id === asked) || open[0])?.id ?? null
}

const spaced = (code: string) => code.replace(/(.{3})(?=.)/g, '$1-')
const errText = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong.')

export function DevicePairSignIn() {
  return (
    <SignInPage
      title="Register a device"
      lead={<>A Dataphyll Field tablet or phone is asking to join CONTROL. Sign in with your CONTROL account to choose the workspace it belongs to.</>}
      notice={
        <div className={signIn.invite}>
          A system user of the workspace can register a device there, and so can its Receipters and Supervisors, with the account they use for
          Dataphyll Crew.
        </div>
      }
    />
  )
}

export function DevicePairPage() {
  const { code = '' } = useParams()
  const { user, signOut } = useAuth()
  const { selectWorkspace } = useWorkspace()
  const navigate = useNavigate()
  const [data, setData] = useState<{ pairing: Pairing; places: Place[] } | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [placeId, setPlaceId] = useState<string | null>(null)
  const [name, setName] = useState('')
  const [deviceType, setDeviceType] = useState<DeviceType['id'] | null>(null)
  const [busy, setBusy] = useState<'approve' | 'deny' | null>(null)
  const [failed, setFailed] = useState<string | null>(null)
  const [done, setDone] = useState<{ place: Place; device: FieldDevice } | null>(null)

  const load = useCallback(async () => {
    setLoadError(null)
    try {
      const r = await apiJson<{ pairing: Pairing; places: Place[] }>(user, `/api/devices/pairings/${encodeURIComponent(code)}`)
      setData(r)
      const open = r.places.filter((p) => p.can_register)
      const first = open.length === 1 ? open[0] : null
      setPlaceId((id) => id ?? first?.id ?? null)
      setName((n) => n || first?.existing?.name || r.pairing.device_id)
      if (first) setDeviceType((t) => t ?? typeFor(first, first.existing?.device_type ?? r.pairing.device_type))
    } catch (err) {
      setLoadError(err instanceof ApiError && err.status === 404 ? 'There is no such code. It may have run out: ask the tablet for a new one.' : errText(err))
    }
  }, [user, code])

  useEffect(() => {
    load()
  }, [load])

  const choose = (p: Place) => {
    setPlaceId(p.id)
    if (p.existing?.name) setName(p.existing.name)
    setDeviceType((t) => (p.device_types.some((x) => x.id === t && x.enabled) ? t : typeFor(p, p.existing?.device_type ?? data?.pairing.device_type)))
  }

  const approve = async () => {
    const place = data?.places.find((p) => p.id === placeId)
    if (!user || !place || !name.trim() || !deviceType) return
    setBusy('approve')
    setFailed(null)
    try {
      // The workspace goes in the body, not as the open one: a Crew Receipter or Supervisor has no CONTROL workspace.
      const bound: BoundSource = { getIdToken: () => user.getIdToken(), uid: user.uid, boundTenantId: null }
      const r = await apiJson<{ device: FieldDevice }>(bound, `/api/devices/pairings/${encodeURIComponent(code)}/approve`, {
        method: 'POST',
        body: { client_id: place.id, name: name.trim(), device_type: deviceType },
      })
      setDone({ place, device: r.device })
    } catch (err) {
      setFailed(errText(err))
      if (err instanceof ApiError && (err.status === 409 || err.status === 410)) load()
    } finally {
      setBusy(null)
    }
  }

  const deny = async () => {
    setBusy('deny')
    setFailed(null)
    try {
      const bound: BoundSource | null = user && { getIdToken: () => user.getIdToken(), uid: user.uid, boundTenantId: null }
      await apiJson(bound, `/api/devices/pairings/${encodeURIComponent(code)}/deny`, { method: 'POST', body: {} })
      await load()
    } catch (err) {
      setFailed(errText(err))
    } finally {
      setBusy(null)
    }
  }

  const openDevices = (id: string) => {
    selectWorkspace(id)
    navigate('/devices')
  }

  const p = data?.pairing
  const places = data?.places || []
  const chosen = places.find((x) => x.id === placeId) || null

  let title = 'Register a device'
  let lead: React.ReactNode = 'Checking the code…'
  if (loadError) {
    title = 'This code did not work'
    lead = loadError
  } else if (done) {
    title = 'Registered'
    lead = (
      <>
        <strong>{done.device.name}</strong> is now a {deviceAppLabel(done.device.device_type).toLowerCase()} of <strong>{done.place.name}</strong>.
        The tablet carries on by itself within a few seconds.
      </>
    )
  } else if (p?.status === 'approved') {
    title = 'Already registered'
    lead = (
      <>
        This device was registered{p.client_name ? <> in <strong>{p.client_name}</strong></> : null} as <strong>{p.device_name}</strong>
        {p.decided_by_email ? ` by ${p.decided_by_email}` : ''}, {when(p.decided_at)}. To move it, ask the tablet for a new code.
      </>
    )
  } else if (p?.status === 'denied') {
    title = 'Turned away'
    lead = `This request was turned down${p.decided_by_email ? ` by ${p.decided_by_email}` : ''}. The tablet can ask again.`
  } else if (p?.status === 'expired') {
    title = 'This code has run out'
    lead = 'Codes last 15 minutes. Ask the tablet for a new one and scan that.'
  } else if (p) {
    lead =
      places.length === 0
        ? 'There is nowhere you can register this device. It takes a system user of the workspace, or one of its Receipters or Supervisors.'
        : 'Choose the workspace this device works for and what it is, give it a name people will recognise, and register it.'
  }

  return (
    <div className={styles.screen} data-screen-label="Register a device">
      <div className={styles.glowWarm} aria-hidden="true" />
      <div className={styles.glowSage} aria-hidden="true" />
      <div className={styles.brand}>
        <span className={styles.brandName}>CONTROL</span>
      </div>
      <div className={styles.content}>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.lead}>{lead}</p>

        {p && (
          <section className={styles.panel} aria-label="The device">
            <div className={styles.panelLabel}>Device</div>
            <dl className={own.facts}>
              <dt>ID</dt>
              <dd>
                <code>{p.device_id}</code>
              </dd>
              <dt>Code</dt>
              <dd>
                <code>{spaced(p.pair_code)}</code>
              </dd>
              {p.model && (
                <>
                  <dt>Model</dt>
                  <dd>
                    {p.model}
                    {p.form_factor ? ` (${p.form_factor})` : ''}
                  </dd>
                </>
              )}
              <dt>App</dt>
              <dd>
                Dataphyll Field
                {p.app_version ? `, version ${p.app_version}` : ''}
              </dd>
            </dl>
          </section>
        )}

        {p?.status === 'pending' && !done && places.length > 0 && (
          <section className={styles.panel} aria-labelledby="pair-places">
            <div id="pair-places" className={styles.panelLabel}>
              Register it in
            </div>
            <div className={own.places} role="radiogroup" aria-labelledby="pair-places">
              {places.map((x) => (
                <label key={x.id} className={`${own.place} ${x.can_register ? '' : own.placeOff}`} data-testid="pair-place">
                  <input type="radio" name="place" value={x.id} disabled={!x.can_register || !!busy} checked={placeId === x.id} onChange={() => choose(x)} />
                  <span>
                    <strong>{x.name}</strong>
                    <span className={own.sub}>
                      {!x.can_register
                        ? 'Your access there cannot add devices'
                        : x.existing
                          ? `Already registered here as ${x.existing.name || x.existing.device_id}. Registering again keeps its history and replaces its sign-in`
                          : x.as === 'crew'
                            ? `You can register it here as a ${x.role}`
                            : 'You can register it here'}
                    </span>
                  </span>
                </label>
              ))}
            </div>
            {places.some((x) => x.can_register) ? (
              <>
                {chosen && (
                  <>
                    <div id="pair-type" className={own.nameLabel}>
                      It is a
                    </div>
                    <div className={own.places} role="radiogroup" aria-labelledby="pair-type">
                      {chosen.device_types.map((t) => (
                        <label key={t.id} className={`${own.place} ${t.enabled ? '' : own.placeOff}`} data-testid="pair-type">
                          <input
                            type="radio"
                            name="device-type"
                            value={t.id}
                            disabled={!t.enabled || !!busy}
                            checked={deviceType === t.id}
                            onChange={() => setDeviceType(t.id)}
                          />
                          <span>
                            <strong>{t.label}</strong>
                            <span className={own.sub}>{t.enabled ? TYPE_BLURB[t.id] : `Not set up in ${chosen.name}. It is turned on in Client Setup`}</span>
                          </span>
                        </label>
                      ))}
                    </div>
                    {!chosen.device_types.some((t) => t.enabled) && (
                      <p className={own.hint}>{chosen.name} has no Dataphyll Field devices set up yet. Ask Dataphyll to turn them on in its Client Setup.</p>
                    )}
                  </>
                )}
                <label className={own.nameLabel} htmlFor="pair-name">
                  Name
                </label>
                <input
                  id="pair-name"
                  className={own.input}
                  value={name}
                  maxLength={80}
                  placeholder="e.g. Packhouse scale 1"
                  disabled={!!busy}
                  onChange={(e) => setName(e.target.value)}
                />
                <p className={own.hint}>Shown in CONTROL and on the tablet. Its ID stays {p.device_id}.</p>
              </>
            ) : (
              <p className={styles.panelText}>None of your workspaces lets you add devices. Ask an administrator there to scan the code.</p>
            )}
            {failed && <Failed text={failed} />}
            <div className={styles.actions}>
              <PendingButton
                className={styles.primary}
                busy={busy === 'approve'}
                pending="Registering…"
                disabled={!chosen?.can_register || !deviceType || !name.trim() || !!busy}
                onClick={approve}
              >
                Register{chosen ? ` in ${chosen.name}` : ''}
              </PendingButton>
              <PendingButton className={styles.secondary} busy={busy === 'deny'} pending="Turning away…" disabled={!!busy} onClick={deny}>
                Turn it away
              </PendingButton>
            </div>
          </section>
        )}

        <div className={styles.actions}>
          {done ? (
            <button type="button" className={styles.primary} onClick={() => openDevices(done.place.id)}>
              See devices in {done.place.name}
            </button>
          ) : (
            <a href="/" className={styles.secondary}>
              Continue to CONTROL
            </a>
          )}
          {!done && (
            <button type="button" className={styles.secondary} onClick={() => signOut().catch(() => {})}>
              Sign in as someone else
            </button>
          )}
        </div>
        <p className={styles.note}>Signed in as {user?.email || 'your account'}.</p>
      </div>
    </div>
  )
}
